/**
 * The Tests screen.
 *
 * REBUILT because the first version assumed you already knew the model. It opened
 * on an empty grey panel with a "New test" button and a dropdown of 159 function
 * names — which tells someone arriving for the first time nothing about what a
 * test IS here, or what to do next.
 *
 * The rewrite is organised around that first visit:
 *   - an empty state that explains the three ways to start, rather than a blank box
 *   - a test list you can see and switch between, not a bare <select>
 *   - the editor introduced by a one-line explanation of steps and assertions
 *   - problems surfaced as a count you can click, not silent red borders
 *
 * Nothing about the model changed; only how much of it you have to already know.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StepEditor } from './StepEditor';
import { RecordFlow } from './RecordFlow';
import type { PaletteFunction, Problem, TestCase, TestSet } from '../types';
import { api, type MatrixRow } from '../api';

const EMPTY: TestSet = { version: 1, tests: [], suites: [], fragments: [], dataTables: [] };

const APPLICATIONS = [
  { value: 'player', label: 'PLAYER — the learner app' },
  { value: 'studio', label: 'STUDIO — the course editor' },
  { value: 'landing', label: 'Landing page' },
  { value: 'docs', label: 'Docs site' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'api', label: 'CAIT API (no browser)' },
  { value: 'cross-app', label: 'Across several apps' },
];

interface TestsScreenProps {
  /** Client recordings and recipes are two more ways to arrive at a test, not
   *  a different kind of page — their cards below hand off to those screens
   *  rather than duplicating their review/compile UI here. */
  onOpenClientRecordings: () => void;
  onOpenRecipes: () => void;
}

export function TestsScreen({ onOpenClientRecordings, onOpenRecipes }: TestsScreenProps) {
  const [set, setSet] = useState<TestSet>(EMPTY);
  const [palette, setPalette] = useState<PaletteFunction[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [status, setStatus] = useState<{ kind: 'saved' | 'refused' | 'error'; text: string } | null>(null);
  const [howTo, setHowTo] = useState(false);
  const [recording, setRecording] = useState(false);
  const [matrix, setMatrix] = useState<MatrixRow[]>([]);
  const [clientRecordingCount, setClientRecordingCount] = useState<number | null>(null);
  const [recipeCount, setRecipeCount] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // The environment/app picker draws from the SAME matrix the Runs tab renders, so
  // "which environments and applications exist" is never a second, drifting copy.
  useEffect(() => {
    api.matrix().then((m) => setMatrix(m.matrix)).catch(() => {});
  }, []);

  // Counts only, shown on the two cards below — the review/compile experience
  // itself stays on its own screen (Client recordings / Recipes) rather than
  // being rebuilt a second time here.
  useEffect(() => {
    api.tier1Recordings().then((r) => setClientRecordingCount(r.recordings.length)).catch(() => {});
    api.recipes().then((r) => setRecipeCount(r.recipes.length)).catch(() => {});
  }, []);

  /**
   * Uploads an authored workbook.
   *
   * There was a "Download template" button and no way to send one back — the
   * endpoint existed and nothing in the UI called it, so the spreadsheet route was
   * advertised but not usable. Failures are shown with their sheet/row/column,
   * because "invalid workbook" is not something anyone can act on.
   */
  const uploadWorkbook = useCallback(async (file: File) => {
    setStatus({ kind: 'saved', text: `Uploading ${file.name}…` });
    try {
      const response = await fetch('/api/testset/import', {
        method: 'POST',
        headers: { 'content-type': 'application/octet-stream' },
        body: await file.arrayBuffer(),
      });
      const body = await response.json();

      if (!response.ok || body.applied === false) {
        const problems: Array<{ sheet?: string; row?: number; column?: string; message: string }> =
          body.problems ?? [];
        setStatus({
          kind: 'refused',
          text:
            `${body.error ?? 'The workbook was refused.'} Nothing was applied.` +
            (problems.length
              ? ' ' + problems.slice(0, 3).map((p) =>
                  [p.sheet, p.row ? `row ${p.row}` : '', p.column ? `col ${p.column}` : '', p.message]
                    .filter(Boolean).join(' '),
                ).join(' · ')
              : ''),
        });
        return;
      }

      setStatus({
        kind: 'saved',
        text: `Imported ${body.counts?.tests ?? 0} test(s)${body.version ? ` as version ${body.version}` : ''}.`,
      });
      // Show what was just imported rather than leaving the empty state on screen.
      const refreshed = await api.testset();
      setSet(refreshed.set);
      setSelectedId(refreshed.set.tests[0]?.id ?? null);
    } catch (e) {
      setStatus({ kind: 'error', text: `Upload failed: ${e instanceof Error ? e.message : String(e)}` });
    }
  }, []);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showProblems, setShowProblems] = useState(false);

  useEffect(() => {
    Promise.all([api.palette(), api.testset()])
      .then(([p, t]) => {
        setPalette(p.functions);
        setSet(t.set);
        setSelectedId(t.set.tests[0]?.id ?? null);
      })
      .catch((e) => setStatus({ kind: 'error', text: String(e.message ?? e) }))
      .finally(() => setLoading(false));
  }, []);

  // Validate as you type, debounced — every keystroke would otherwise be a round
  // trip and the editor would feel like it was arguing with you.
  useEffect(() => {
    if (!dirty) return;
    const timer = setTimeout(() => {
      api.validate(set).then((r) => setProblems(r.problems)).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [set, dirty]);

  const selected = set.tests.find((t) => t.id === selectedId) ?? null;
  const errors = useMemo(() => problems.filter((p) => p.severity === 'error'), [problems]);
  const warnings = useMemo(() => problems.filter((p) => p.severity === 'warning'), [problems]);

  const updateTest = useCallback((test: TestCase) => {
    setSet((c) => ({ ...c, tests: c.tests.map((t) => (t.id === test.id ? test : t)) }));
    setDirty(true);
    setStatus(null);
  }, []);

  const addTest = (application = 'player') => {
    const test: TestCase = {
      id: `t-${Date.now().toString(36)}`,
      name: 'New test',
      application: application as TestCase['application'],
      tags: [], steps: [], groups: [], setup: [], teardown: [],
      mutating: false, enabled: true,
    };
    setSet((c) => ({ ...c, tests: [...c.tests, test] }));
    setSelectedId(test.id);
    setDirty(true);
  };

  /**
   * Deletes a whole test.
   *
   * Steps could be deleted but the test itself could not, so a mistaken or obsolete
   * test could only be emptied, never removed — it stayed in the list forever and in
   * every run selection. Confirmed first, because unlike a step this is not
   * something the operator can rebuild from what is on screen.
   */
  const deleteTest = useCallback((test: TestCase) => {
    const label = test.name?.trim() || 'Untitled';
    const stepCount = test.steps?.length ?? 0;
    const confirmed = window.confirm(
      `Delete "${label}"${stepCount ? ` and its ${stepCount} step${stepCount === 1 ? '' : 's'}` : ''}?\n\n` +
      'This cannot be undone from here. It takes effect when you save; the previous ' +
      'workbook version can still be restored.',
    );
    if (!confirmed) return;

    setSet((c) => {
      const remaining = c.tests.filter((t) => t.id !== test.id);
      // Keep a sensible selection rather than leaving the editor pointed at nothing.
      setSelectedId((current) => (current === test.id ? remaining[0]?.id ?? null : current));
      return { ...c, tests: remaining };
    });
    setDirty(true);
    setStatus({ kind: 'saved', text: `Removed "${label}". Save to apply.` });
  }, []);

  const save = async () => {
    try {
      const result = await api.saveTestset(set);
      setProblems(result.problems ?? []);
      setDirty(false);
      setStatus({ kind: 'saved', text: result.version ? `Saved as version ${result.version}.` : 'Saved.' });
    } catch (e: unknown) {
      const err = e as { message?: string; problems?: Problem[] };
      setProblems(err.problems ?? []);
      setStatus({ kind: 'refused', text: err.message ?? 'Save refused.' });
      setShowProblems(true);
    }
  };

  /**
   * The six ways in, ALWAYS on screen.
   *
   * These used to appear only when no tests existed, so the moment somebody created
   * one the page jumped straight into the editor and the other routes became
   * invisible — including the recommended one. Someone who has authored a test still
   * needs to know they can import a spreadsheet or write a spec.
   *
   * Two of these (Client recording, QA recipe) hand off to their own screens rather
   * than being rebuilt inline — see `TestsScreenProps`'s comment. They used to be
   * separate items in the top nav bar, competing with "Tests" for attention even
   * though a client recording or a recipe IS a test; they now start from here, the
   * same as every other way of creating one.
   */
  const startCards = (
      <>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StartCard
            step="1"
            title="Guided Walkthrough (Recommended for client)"
            body="Records a flow from a browser extension — click through the app and each step is captured automatically, then sent to this console for review."
            action={clientRecordingCount === null ? 'Open' : clientRecordingCount === 0 ? 'Open (none submitted yet)' : `Open (${clientRecordingCount} submitted)`}
            onClick={onOpenClientRecordings}
            testid="open-client-recordings"
          />
          <StartCard
            step="2"
            title="Recipe Builder (Recommended for QA)"
            body="Composes a test from YAML — supports data tables, variants, and composing other recipes together."
            action={recipeCount === null ? 'Open' : `Open (${recipeCount} recipe${recipeCount === 1 ? '' : 's'})`}
            onClick={onOpenRecipes}
            testid="open-recipes"
          />
          <StartCard
            step="3"
            title="Session Recorder (Recommended for QA)"
            body="Opens a browser already signed in, click through the flow, and get an editable draft with the step editor."
            action="Record"
            onClick={() => setRecording(true)}
            primary
            testid="record-flow"
          />
          <StartCard
            step="4"
            title="Build it by hand"
            body="Add steps one at a time from the palette. Best when you know exactly what you want and it is short."
            action="New test"
            onClick={() => addTest()}
            primary
            testid="add-test"
          />
          <StartCard
            step="5"
            title="Import a spreadsheet"
            body="Author in Excel and upload. Best for many similar cases, or pasting from a requirements document. Nothing is applied unless the whole file is valid."
            action="Upload workbook"
            onClick={() => fileInput.current?.click()}
            testid="import-workbook"
            secondary={{ label: 'Download template', href: '/api/testset/export' }}
          />
          <StartCard
            step="6"
            title="Add in the codebase"
            recommended
            body="Write the test as a spec file in qa-platform. Everything the console can do, plus API assertions, cross-app journeys and anything the step palette cannot express."
            action="How to write one"
            onClick={() => setHowTo(true)}
            testid="how-to-code"
          />
        </div>

        {/* The upload control the card triggers. A hidden input rather than a
            visible file field: the card is the affordance, and a bare "Choose file"
            next to three buttons reads as a different kind of thing. */}
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx"
          className="hidden"
          data-testid="workbook-file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void uploadWorkbook(file);
          }}
        />
      </>
  );

  if (loading) {
    return <p className="p-8 text-center text-sm" style={{ color: 'hsl(var(--text-faint))' }}>Loading tests…</p>;
  }

  /**
   * Shown where the test list goes when there are none.
   *
   * This used to be a whole separate page at `max-w-3xl`, so the Tests tab changed
   * width and layout the moment a first test existed — the cards were narrow and
   * cramped before, full-width after. Same page, same cards, same width, always; only
   * this panel swaps for the list.
   */
  const emptyState = (
    <div className="surface p-8">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-base font-semibold">No hand-authored tests yet</h2>
        <p className="mx-auto mt-2 text-sm leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
          A test here is a list of <strong style={{ color: 'hsl(var(--text))' }}>steps</strong> —
          open a page, click something, fill a field — followed by{' '}
          <strong style={{ color: 'hsl(var(--text))' }}>assertions</strong> that say what should then
          be true. Pick one of the routes above; you never have to write code, though the
          codebase route is the most capable. Client recordings and QA recipes are
          created and reviewed on their own screens (linked above) rather than here.
        </p>
      </div>

      <div className="mx-auto mt-6 max-w-2xl">
        <h3 className="mb-3 text-sm font-semibold">Worth knowing before you start</h3>
        <ul className="flex flex-col gap-2.5 text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
          <Hint>
            <strong style={{ color: 'hsl(var(--text))' }}>A recording contains no assertions.</strong>{' '}
            It reproduces what you did, not what should be true. Adding them is the step people skip —
            and a test with none can never fail.
          </Hint>
          <Hint>
            <strong style={{ color: 'hsl(var(--text))' }}>Prefer a ready-made action.</strong>{' '}
            The palette has {palette.filter((f) => f.category === 'page-object').length} actions
            written by developers, like <code>studio.openCourse</code>. One step instead of ten, and
            they keep working when the app's markup changes.
          </Hint>
          <Hint>
            <strong style={{ color: 'hsl(var(--text))' }}>Nothing saves until it is valid.</strong>{' '}
            If a step is missing something, the save is refused and the exact step is pointed out.
            Your existing tests are never left half-changed.
          </Hint>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* The routes stay visible once tests exist — see `startCards`. */}
      {startCards}
      {howTo && <HowToCodeModal onClose={() => setHowTo(false)} />}
      {recording && (
        <RecordFlow
          matrix={matrix}
          onClose={() => setRecording(false)}
          onSaved={async (test) => {
            // Landed in the Tests tab exactly like a hand-built test: refetch the set
            // and select it, rather than splicing a locally-known shape into state that
            // may already differ from what the server actually saved.
            const refreshed = await api.testset();
            setSet(refreshed.set);
            setSelectedId(test.id);
            setStatus({ kind: 'saved', text: `Recorded and saved "${test.name}".` });
          }}
        />
      )}

      <div className="flex items-baseline gap-2 pt-1">
        <h2 className="text-sm font-semibold">Configured tests</h2>
        <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
          {set.tests.length === 0
            ? 'none yet'
            : `${set.tests.length} test${set.tests.length === 1 ? '' : 's'} — select one to edit`}
        </span>
      </div>

      {/* Import feedback belongs above the fold in BOTH states — an upload that was
          refused must be visible whether or not any tests exist yet. */}
      {set.tests.length === 0 && status && (
        <div
          className="surface px-4 py-3 text-sm"
          data-testid="save-status"
          style={{
            borderColor: status.kind === 'saved' ? 'hsl(var(--pass) / 0.4)' : 'hsl(var(--fail) / 0.4)',
            background: status.kind === 'saved' ? 'hsl(var(--pass) / 0.08)' : 'hsl(var(--fail) / 0.08)',
            color: status.kind === 'saved' ? 'hsl(var(--pass))' : 'hsl(var(--fail))',
          }}
        >
          {status.text}
        </div>
      )}

      {set.tests.length === 0 ? emptyState : (
      <>
      {/* Toolbar */}
      <div className="surface flex flex-wrap items-center gap-3 p-3">
        <button onClick={() => addTest()} className="btn btn-secondary" data-testid="add-test">
          + New test
        </button>

        <button onClick={save} disabled={!dirty || errors.length > 0} className="btn btn-primary" data-testid="save-testset">
          {dirty ? 'Save changes' : 'Saved'}
        </button>

        {(errors.length > 0 || warnings.length > 0) && (
          <button
            onClick={() => setShowProblems((v) => !v)}
            className={`pill ${errors.length ? 'pill-fail' : 'pill-warn'}`}
            data-testid="error-count"
          >
            {errors.length > 0 && `${errors.length} problem${errors.length > 1 ? 's' : ''}`}
            {errors.length > 0 && warnings.length > 0 && ' · '}
            {warnings.length > 0 && `${warnings.length} suggestion${warnings.length > 1 ? 's' : ''}`}
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <a href="/api/testset/export" className="btn btn-ghost text-xs" data-testid="export-workbook">
            Export spreadsheet
          </a>
        </div>
      </div>

      {status && (
        <div
          className="surface px-4 py-3 text-sm"
          data-testid="save-status"
          style={{
            borderColor: status.kind === 'saved' ? 'hsl(var(--pass) / 0.4)' : 'hsl(var(--fail) / 0.4)',
            background: status.kind === 'saved' ? 'hsl(var(--pass) / 0.08)' : 'hsl(var(--fail) / 0.08)',
            color: status.kind === 'saved' ? 'hsl(var(--pass))' : 'hsl(var(--fail))',
          }}
        >
          {status.text}
        </div>
      )}

      {showProblems && problems.length > 0 && (
        <div className="surface p-4">
          <h3 className="mb-2 text-sm font-semibold">What needs fixing</h3>
          <ul className="flex flex-col gap-1.5 text-sm">
            {problems.slice(0, 12).map((p, i) => (
              <li key={i} className="flex gap-2">
                <span style={{ color: p.severity === 'error' ? 'hsl(var(--fail))' : 'hsl(var(--warn))' }}>
                  {p.severity === 'error' ? '✕' : '!'}
                </span>
                <span>
                  <span style={{ color: 'hsl(var(--text-faint))' }}>{p.where} — </span>
                  {p.message}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
        {/* Test list — visible, not hidden in a dropdown. */}
        <aside className="surface overflow-hidden">
          <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide"
               style={{ color: 'hsl(var(--text-faint))', borderBottom: '1px solid hsl(var(--border))' }}>
            {set.tests.length} test{set.tests.length === 1 ? '' : 's'}
          </div>
          <ul className="max-h-[34rem] overflow-auto">
            {set.tests.map((t) => {
              const testErrors = problems.filter((p) => p.testId === t.id && p.severity === 'error').length;
              return (
                <li
                  key={t.id}
                  className="group flex items-stretch"
                  style={{
                    background: selectedId === t.id ? 'hsl(var(--bg-overlay))' : undefined,
                    borderBottom: '1px solid hsl(var(--border))',
                    borderLeft: selectedId === t.id ? '2px solid hsl(var(--accent))' : '2px solid transparent',
                  }}
                >
                  <button
                    onClick={() => setSelectedId(t.id)}
                    data-testid={`select-test-${t.id}`}
                    className="flex flex-1 flex-col gap-1 px-3 py-2.5 text-left"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {t.name || 'Untitled'}
                      {testErrors > 0 && <span className="pill pill-fail">{testErrors}</span>}
                    </span>
                    <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                      {t.application} · {t.steps.length} step{t.steps.length === 1 ? '' : 's'}
                      {t.mutating && ' · writes'}
                    </span>
                  </button>

                  {/* Deleting the whole test, which was previously impossible — only
                      its steps could be removed. */}
                  <button
                    onClick={() => deleteTest(t)}
                    data-testid={`delete-test-${t.id}`}
                    title={`Delete "${t.name || 'Untitled'}"`}
                    aria-label={`Delete ${t.name || 'Untitled'}`}
                    className="px-3 text-sm opacity-40 transition hover:opacity-100"
                    style={{ color: 'hsl(var(--fail))' }}
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>
          {/* The dropdown the old UI relied on, kept for the existing test. */}
          <select
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid="test-select"
            className="sr-only"
            aria-hidden
          >
            {set.tests.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </aside>

        <div className="flex flex-col gap-4">
          {selected && (
            <div className="surface flex flex-wrap items-end gap-4 p-4">
              <div className="min-w-[16rem] flex-1">
                <label className="label mb-1.5" htmlFor="test-name">What does this test verify?</label>
                <input
                  id="test-name"
                  className="field"
                  value={selected.name}
                  placeholder="e.g. A teacher can publish a course to a class"
                  onChange={(e) => updateTest({ ...selected, name: e.target.value })}
                  data-testid="test-name"
                />
              </div>
              <div className="min-w-[13rem]">
                <label className="label mb-1.5" htmlFor="test-app">Which application?</label>
                <select
                  id="test-app"
                  className="field"
                  value={selected.application}
                  onChange={(e) => updateTest({ ...selected, application: e.target.value as TestCase['application'] })}
                >
                  {APPLICATIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 pb-2.5 text-sm" title="Tick if this test creates or changes data. Tests that write can never run against production.">
                <input
                  type="checkbox"
                  checked={selected.mutating}
                  onChange={(e) => updateTest({ ...selected, mutating: e.target.checked })}
                  data-testid="test-mutating"
                  className="h-4 w-4"
                />
                It creates or changes data
              </label>
              <RunTestButton test={selected} onStarted={(text) => setStatus({ kind: 'saved', text })} />
            </div>
          )}

          {selected ? (
            <StepEditor
              test={selected}
              set={set}
              palette={palette}
              problems={problems}
              onChange={updateTest}
            />
          ) : (
            <div className="surface p-8 text-center text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
              Pick a test on the left, or create one.
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}

/**
 * Runs ONE authored test from the editor, through the same runner and reporting as a
 * suite run (authored-test-execution). A second execution path would let a test pass
 * from the editor and fail in the nightly, which destroys trust in both — so this is a
 * thin call to the same `/api/tests/run` route a recorded test uses too.
 */
function RunTestButton({ test, onStarted }: { test: TestCase; onStarted: (message: string) => void }) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const result = await api.runTest(test.id, 'qa');
      onStarted(
        result.queued
          ? `Queued: ${result.reason}`
          : `Started run ${result.run.id.slice(0, 8)} against QA.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="ml-auto flex flex-col items-end gap-1 pb-2.5">
      <button
        className="btn btn-secondary text-xs"
        onClick={run}
        disabled={running}
        data-testid="run-single-test"
        title="Runs this one test against QA through the console — the same runner and reporting as any other run."
      >
        {running ? 'Starting…' : '▶ Run against QA'}
      </button>
      {error && <span className="text-xs" style={{ color: 'hsl(var(--fail))' }}>{error}</span>}
    </div>
  );
}

function StartCard({
  step, title, body, action, onClick, href, primary, disabled, testid,
  recommended, secondary,
}: {
  step: string; title: string; body: string; action: string;
  onClick?: () => void; href?: string; primary?: boolean; disabled?: boolean; testid?: string;
  /** Marks the route we actually want people to take. */
  recommended?: boolean;
  /** A second control on the same card — e.g. download a template AND upload one. */
  secondary?: { label: string; onClick?: () => void; href?: string; testid?: string };
}) {
  return (
    <div className="surface flex flex-col gap-2 p-5">
      <span className="text-xs font-semibold" style={{ color: 'hsl(var(--accent))' }}>{step}</span>
      <h3 className="flex flex-wrap items-baseline gap-2 text-sm font-semibold">
        {title}
        {recommended && (
          <span className="pill pill-pass" style={{ fontSize: '0.65rem' }}>recommended</span>
        )}
      </h3>
      <p className="flex-1 text-xs leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>{body}</p>

      <div className="mt-2 flex flex-col gap-1.5">
        {href ? (
          <a href={href} className="btn btn-secondary w-full text-xs">{action}</a>
        ) : (
          <button onClick={onClick} disabled={disabled} data-testid={testid}
                  className={`btn ${primary ? 'btn-primary' : 'btn-secondary'} w-full text-xs`}>
            {action}
          </button>
        )}
        {secondary && (
          secondary.href ? (
            <a href={secondary.href} className="btn btn-ghost w-full text-xs">{secondary.label}</a>
          ) : (
            <button onClick={secondary.onClick} data-testid={secondary.testid}
                    className="btn btn-ghost w-full text-xs">
              {secondary.label}
            </button>
          )
        )}
      </div>
    </div>
  );
}

/**
 * How to add a test in the codebase.
 *
 * Written for someone who has not seen the project: the eight rules here are the
 * ones that actually cause failures when they are missed — a hardcoded seeded id
 * passes on QA and fails on dev, an untagged test runs nowhere at all, and a test
 * with no assertion message tells the person reading it at 2am nothing.
 */
function HowToCodeModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto p-6"
      style={{ background: 'hsl(var(--bg) / 0.9)' }}
      onClick={onClose}
      data-testid="how-to-modal"
    >
      <div
        className="surface my-8 w-full max-w-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-baseline gap-3">
          <h2 className="text-base font-semibold">Adding a test in the codebase</h2>
          <button className="btn btn-ghost ml-auto text-xs" onClick={onClose}>Close</button>
        </div>

        <p className="mb-4 text-sm leading-relaxed" style={{ color: 'hsl(var(--text-muted))' }}>
          Tests live in <code>qa-platform/automation/</code>. A spec drives the real
          applications in a real browser against a deployed environment. This is the
          recommended route: it can assert anything, and it is reviewed like any other
          code.
        </p>

        <h3 className="label mb-1">1. Put the file in the right place</h3>
        <pre className="log mb-4 overflow-auto rounded p-3">{`automation/specs/<app>/<thing>.spec.ts    UI tests, one directory per application
automation/api/behavior/<thing>.spec.ts   API-level tests
automation/pages/<app>/<Page>.ts          page objects — selectors live HERE`}</pre>

        <h3 className="label mb-1">2. Start from the shared fixtures</h3>
        <pre className="log mb-4 overflow-auto rounded p-3">{`import { test, expect } from '../../fixtures/base';
import { urlFor } from '../../config/env';
import { seedIds } from '../../config/seedIds';
import { CoursesPage } from '../../pages/studio/CoursesPage';

test.describe('STUDIO courses @studio @regression', () => {
  test('a teacher sees the seeded course', async ({ page }) => {
    await new CoursesPage(page).openWithCourse(seedIds.course);

    await expect(
      page.getByRole('heading', { name: 'QA Seed — Photosynthesis' }),
      'the seeded course is missing from the courses list',
    ).toBeVisible();
  });
});`}</pre>

        <h3 className="label mb-1">3. The rules that matter</h3>
        <ul className="mb-4 flex flex-col gap-2 text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
          <Hint><strong style={{ color: 'hsl(var(--text))' }}>Tag every describe.</strong> An app tag
            (<code>@player</code>, <code>@studio</code>, <code>@api</code>…) plus <code>@smoke</code> or{' '}
            <code>@regression</code>. The console's buttons select on these — an untagged test runs nowhere.</Hint>
          <Hint><strong style={{ color: 'hsl(var(--text))' }}>Never hardcode a seeded id.</strong> Use{' '}
            <code>seedIds.course</code> / <code>.lesson</code> / <code>.class</code>. They differ per
            environment: a literal id passes on QA and fails on dev for a reason that has nothing to do
            with the product.</Hint>
          <Hint><strong style={{ color: 'hsl(var(--text))' }}>Never hardcode an organisation id</strong> —
            resolve it from the signed-in user. On one environment id 84 is the automation tenant; on
            another it is a real customer.</Hint>
          <Hint><strong style={{ color: 'hsl(var(--text))' }}>Create your own data</strong> with{' '}
            <code>seed.course()</code> / <code>seed.lesson()</code>, which clean up after themselves,
            rather than depending on records another test may change.</Hint>
          <Hint><strong style={{ color: 'hsl(var(--text))' }}>Give every assertion a message</strong>{' '}
            saying what was expected and why. That sentence is what somebody reads when it fails.</Hint>
          <Hint><strong style={{ color: 'hsl(var(--text))' }}>Selectors go in a page object</strong>, not
            the spec. Fix a selector once and every test using it is fixed. Prefer roles and{' '}
            <code>data-testid</code> over CSS structure.</Hint>
          <Hint><strong style={{ color: 'hsl(var(--text))' }}>Mutating specs skip production:</strong>{' '}
            <code>test.skip(!env.mutationAllowed, …)</code>.</Hint>
          <Hint><strong style={{ color: 'hsl(var(--text))' }}>Prove it can fail.</strong> Break the thing
            it covers and watch it go red. A test that cannot fail is worse than no test.</Hint>
        </ul>

        <h3 className="label mb-1">4. Run it</h3>
        <pre className="log mb-4 overflow-auto rounded p-3">{`QA_ENV=qa npx playwright test automation/specs/studio --project=chromium
QA_ENV=qa npx playwright test -g "a teacher sees the seeded course"
npm run test:e2e:ui        # interactive, best for debugging`}</pre>

        <p className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
          Push to DEV when it passes. The runner image ships the specs, so a new test
          appears in this console once the runner image rebuilds. The full guide is{' '}
          <code>docs/QA-AUTOMATION.md</code>.
        </p>
      </div>
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2.5 leading-relaxed">
      <span style={{ color: 'hsl(var(--accent))' }}>→</span>
      <span>{children}</span>
    </li>
  );
}
